import { useQuery, useQueryClient } from "@tanstack/react-query";
import deliveryCore from "../../core/delivery.core";
import ErrorCenterIcon from "../../icons/exclamationTriangleCenter.icon";
import LoadingOvalCenterIcon from "../../icons/loadingOvalCenter.icon";
import useDeliveryVehichles from "../../hooks/useDeliveryVehiclesList.hook";
import TruckIcon from "../../icons/truck.icon";
import { useForm, UseFormReturn, useWatch } from "react-hook-form";
import {
  DeliveryLogisticItem,
  PendingDeliveryScheduleFormData,
  GroupedDeliverySchedule,
  DeliverySchedule,
} from "../../types/delivery";
import Link from "next/link";
import { useDroppable } from "@dnd-kit/core";
import Accordion from "../common/accordion.comp";
import UpdateBadge from "../form/updateBadge.comp";
import { toast } from "react-toastify";
import { useTranslation } from "../../core/translation.core";
import { FancyCheckbox, LabelAndTextArea } from "../reuseables/form.comp";
import { useMemo } from "react";
import DeleteIcon from "../../icons/delete.icon";
import useGetOrder from "../../hooks/useGetOrder.hook";
import shipCore from "../../core/ship.core";
import { useRouter } from "next/router";
import DeliveryOrderDownload from "./deliveryOrderDownload.comp";
import GiftIcon from "../../icons/gift.icon";
import { BundleProductBadge } from "./bundleProductBadge";
import React from "react";
import { LogisticShippingEditorForm } from "./logisticShippingEditor.comp";

interface deliveryVehicleScheduleSelectorProps {
  methods: UseFormReturn<PendingDeliveryScheduleFormData>;
}

export default function DeliveryScheduleList({ methods }: deliveryVehicleScheduleSelectorProps) {
  const { data: vehicles } = useDeliveryVehichles();
  const { t, msg } = useTranslation();

  const { data: deliveryScheduleGroups, isPending, isError, failureReason } = useDeliverySchedulesGroups();

  if (isPending) return <LoadingOvalCenterIcon />;
  if (isError) return <ErrorCenterIcon reason={failureReason} />;
  if (!deliveryScheduleGroups) return null;

  return (
    <div id="delivery-vehicle-selector">
      {vehicles && vehicles?.length > 0 ? (
        deliveryScheduleGroups
          .sort((a, b) => {
            return a.date.localeCompare(b.date);
          })
          .map((deliveryScheduleGroup) => (
            <DeliveryScheduleGroupComp
              deliveryScheduleGroup={deliveryScheduleGroup}
              methods={methods}
              key={`${deliveryScheduleGroup.date}-${deliveryScheduleGroup.schedules[0].id}`}
            />
          ))
      ) : (
        <p>
          <Link href="/delivery/vehicleCreate" className="text-her-blue underline">
            {t(msg.delivery.addAVehicle)}
          </Link>
        </p>
      )}
    </div>
  );
}

interface DailyVehiclesListProps {
  deliveryScheduleGroup: GroupedDeliverySchedule;
  methods: UseFormReturn<PendingDeliveryScheduleFormData>;
}

const DeliveryScheduleGroupComp = ({ deliveryScheduleGroup, methods }: DailyVehiclesListProps) => {
  const [vehicleId, shipDate] = useWatch({ name: ["vehicleId", "shipDate"], control: methods.control });
  return (
    <div data-schedule-date={deliveryScheduleGroup.date} data-schedule-vechicle={deliveryScheduleGroup.vechicle.id}>
      <p className="text-end text-xs">{deliveryScheduleGroup.date}</p>
      {deliveryScheduleGroup.schedules.map((schedule) => (
        <Accordion
          key={schedule.id}
          title={
            <DailyVehicleItem
              key={schedule.id}
              date={schedule.shipDate!}
              item={schedule}
              methods={methods}
              isActive={schedule.vehicle?.id === vehicleId && schedule.shipDate === shipDate}
            />
          }
        >
          <div className="flex flex-col gap-2" data-role="delivery-Logistic-items">
            <div className="px-4 pt-4">
              <DeliveryOrderDownload item={schedule} />
            </div>

            {schedule.payload.map((vi) => (
              <DeliveryScheduleItemComp key={vi.trackingId} logisticItem={vi} />
            ))}
          </div>
        </Accordion>
      ))}
    </div>
  );
};

interface DailyVehicleItemProps {
  date: string;
  item: DeliverySchedule;
  methods: UseFormReturn<PendingDeliveryScheduleFormData>;
  isActive: boolean;
}

function DailyVehicleItem({ methods, date, item }: DailyVehicleItemProps) {
  const { t, msg } = useTranslation();
  const vid = item.vehicle?.id ?? "";
  const { isOver, setNodeRef } = useDroppable({
    id: `${item.id}-${date}`,
    data: {
      vehicleId: vid,
      shipDate: date,
    },
  });

  return (
    <div
      ref={setNodeRef}
      className={`flex gap-5 items-center px-3 cursor-pointer ${isOver ? "border border-her-blue/55 rounded-md " : ""}`}
      onClick={() => {
        methods.setValue("shipDate", date);
        methods.setValue("vehicleId", vid);
      }}
    >
      <TruckIcon className="w-8 h-8 text-her-robinEggBlue" />
      <div>
        <p>{item.vehicle?.name}</p>
        <p className="text-sm">
          {item.payload.length} {t(msg.delivery.items)}
        </p>
      </div>
    </div>
  );
}

interface UpdateScheduleFormData {
  note?: string;
  hasRecycle: boolean;
  frontDoorDropOff: boolean;
}

function DeliveryScheduleItemComp({ logisticItem }: { logisticItem: DeliveryLogisticItem }) {
  const { t, msg } = useTranslation();
  const queryClient = useQueryClient();

  const methods = useForm<UpdateScheduleFormData>({
    defaultValues: {
      note: logisticItem.note,
      hasRecycle: logisticItem.hasRecycle,
      frontDoorDropOff: logisticItem.frontDoorDropOff,
    },
  });

  const options = useDeliveryStatusOptions();

  async function onSubmit(formData: UpdateScheduleFormData) {
    try {
      await deliveryCore.updateLogisticItem({
        trackingId: logisticItem.trackingId,
        status: logisticItem.items[0]?.deliveryStatus || "scheduled",
        note: formData.note || "",
        hasRecycle: formData.hasRecycle,
        frontDoorDropOff: formData.frontDoorDropOff,
      });

      queryClient.invalidateQueries({ queryKey: ["scheduledLogisticItems"] });
      toast.success(t(msg.import.success));
    } catch (error) {
      console.error(error);
      toast.error(t(msg.errors.contactAdmin));
    }
  }

  async function updateStatus(status: string) {
    deliveryCore
      .updateLogisticItem({
        trackingId: logisticItem.trackingId,
        status,
        note: logisticItem.note,
        hasRecycle: logisticItem.hasRecycle,
        frontDoorDropOff: logisticItem.frontDoorDropOff,
      })
      .then((_) => {
        queryClient.invalidateQueries({ queryKey: ["scheduledLogisticItems"] });
        toast.success(t(msg.import.success));
      })
      .catch((_) => {
        toast.error(t(msg.errors.contactAdmin));
      });
  }

  async function removeLogisticItem() {
    deliveryCore
      .removeLogisticItem({
        trackingId: logisticItem.trackingId,
      })
      .then((_) => {
        queryClient.invalidateQueries({ queryKey: ["scheduledLogisticItems"] });
        queryClient.invalidateQueries({ queryKey: ["pendingLogisticItems"] });
        toast.success(t(msg.import.success));
      })
      .catch((_) => {
        toast.error(t(msg.errors.contactAdmin));
      });
  }

  if (!logisticItem) return null;

  return (
    <div className="relative" data-role="delivery-Logistic-item" data-tracking-id={logisticItem.trackingId}>
      <button
        type="button"
        data-role="delete"
        className="absolute top-3 right-0 text-red-500 px-3 py-1 rounded-lg"
        onClick={removeLogisticItem}
      >
        <DeleteIcon />
      </button>
      <div className="grid grid-cols-[100px_auto] gap-2 w-full border-b last-of-type:border-none p-4 pb-3">
        <>
          <p>{t(msg.order.order)}: </p>
          <OrderNumbers logisticItem={logisticItem} />
        </>
        <ShippingOption logisticItem={logisticItem} />
        <>
          <p>{t(msg.delivery.trackingId)}: </p>
          <p>{logisticItem.trackingId}</p>
        </>
        <div className="col-span-2" data-logistic-shipping-editor>
          <LogisticShippingEditorForm logisticItem={logisticItem} />
        </div>
        <>
          <p>{t(msg.delivery.status)}:</p>
          {logisticItem.items && (
            <div data-role="update-status" data-deliverystatus={logisticItem.items[0]?.deliveryStatus || "scheduled"}>
              <UpdateBadge
                options={options}
                currentValue={logisticItem.items[0]?.deliveryStatus || "scheduled"}
                onUpdate={updateStatus}
              />
            </div>
          )}
        </>
        <div className="col-span-2">
          {logisticItem.items?.map((item) => (
            <LogisticItemRow
              key={`${item.orderId}-${item.orderProductId}`}
              orderProductId={item.orderProductId}
              orderId={item.orderId}
              trackingId={logisticItem.trackingId}
            />
          ))}
        </div>
        <div className="col-span-2">
          <FancyCheckbox register={methods.register("hasRecycle")}>{t(msg.delivery.hasRecycle)}</FancyCheckbox>
        </div>
        <div className="col-span-2">
          <FancyCheckbox register={methods.register("frontDoorDropOff")}>{t(msg.delivery.frontDoorDropOff)}</FancyCheckbox>
        </div>
        <div className="col-span-2">
          <LabelAndTextArea
            register={methods.register}
            fieldName="note"
            label={t(msg.order.note)}
            rows={5}
          />
          <UpdateSubmitButton methods={methods} logisticItem={logisticItem} onSave={onSubmit} />
        </div>
      </div>
    </div>
  );
}

interface UpdateSubmitButtonProps {
  methods: UseFormReturn<UpdateScheduleFormData>;
  logisticItem: DeliveryLogisticItem;
  onSave: (formData: UpdateScheduleFormData) => void | Promise<void>;
}

function UpdateSubmitButton({ methods, logisticItem, onSave }: UpdateSubmitButtonProps) {
  const { t, msg } = useTranslation();

  const [note, hasRecycle, frontDoorDropOff] = useWatch({
    control: methods.control,
    name: ["note", "hasRecycle", "frontDoorDropOff"],
  });

  const hasChanged = useMemo(
    () =>
      note !== logisticItem.note || hasRecycle !== logisticItem.hasRecycle || frontDoorDropOff !== logisticItem.frontDoorDropOff,
    [note, hasRecycle, frontDoorDropOff, logisticItem]
  );

  if (!hasChanged) return null;

  return (
    <button
      type="button"
      onClick={() => void methods.handleSubmit(onSave)()}
      className="w-full bg-her-blue dark:bg-her-blue text-white p-3 rounded-lg hover:opacity-90 transition-opacity"
    >
      {t(msg.form.update)}
    </button>
  );
}

function OrderNumbers({ logisticItem }: { logisticItem: DeliveryLogisticItem }) {
  const ids = [...new Set((logisticItem.items ?? []).map((i) => i.orderId).filter(Boolean))];

  return (
    <div className="font-bold text-her-greenSheen flex flex-wrap gap-2">
      {ids.map((oid) => (
        <OrderNumberLink key={oid} orderId={oid} />
      ))}
    </div>
  );
}

function OrderNumberLink({ orderId }: { orderId: string }) {
  const { data: order } = useGetOrder(orderId);
  if (!order) return null;
  return <span>{order.orderNumber}</span>;
}

const LogisticItemRow = ({
  orderProductId,
  orderId,
  trackingId,
}: {
  orderProductId: string;
  orderId: string;
  trackingId: string;
}) => {
  const { t, msg } = useTranslation();
  const queryClient = useQueryClient();
  const { data: order } = useGetOrder(orderId);

  async function removeLogisticOrderItem(orderProductId: string, trackingId: string) {
    deliveryCore
      .removeLogisticOrderItem({
        trackingId,
        orderProductId,
      })
      .then((_) => {
        queryClient.invalidateQueries({ queryKey: ["scheduledLogisticItems"] });
        queryClient.invalidateQueries({ queryKey: ["pendingLogisticItems"] });
        toast.success(t(msg.import.success));
      })
      .catch((_) => {
        toast.error(t(msg.errors.contactAdmin));
      });
  }

  if (!order) return null;

  const orderItem = order.products.find((p) => p.orderProductId === orderProductId);
  if (!orderItem) return null;

  return (
    <div className="w-full py-1">
      <div className="flex justify-between">
        <div className="flex gap-2 items-center">
          {orderItem.name}
          {orderItem.isGiftProduct && <GiftIcon className="text-her-paradisePink w-6 h-6" />}
          <BundleProductBadge orderProduct={orderItem} />
        </div>

        <button onClick={() => removeLogisticOrderItem(orderProductId, trackingId)} className="text-red-400 text-xs">
          <DeleteIcon className="w-5 h-5" />
        </button>
      </div>
      {orderItem.productType === "bundle" && (
        <div className="ml-4 grid grid-cols-[auto_minmax(40px,1fr)] gap-2">
          {orderItem.bundleProducts?.map((bp) => (
            <React.Fragment key={bp.orderProductId}>
              <p key={bp.orderProductId}>{bp.name}</p>
              <p>x {bp.quantity * orderItem.quantity}</p>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

function ShippingOption({ logisticItem }: { logisticItem: DeliveryLogisticItem }) {
  const { t, msg } = useTranslation();
  const router = useRouter();

  const { data: shipSettings } = useQuery({
    queryKey: ["delivery-shipOptions"],
    queryFn: () => shipCore.getShipSettings(),
    enabled: router.isReady,
  });

  const firstOrderId = logisticItem.items[0]?.orderId;
  const { data: order } = useGetOrder(firstOrderId || "");

  if (!shipSettings) return null;

  const optId = logisticItem.shipOptionId || order?.shipId;
  const shipOption = optId ? shipSettings.flatMap((s) => s.options).find((option) => option.id === optId) : undefined;

  return (
    <>
      <p className="basis-[120px]">{t(msg.ship.shipType)}: </p>
      <p>{shipOption?.name ?? "—"}</p>
    </>
  );
}

function useDeliverySchedulesGroups() {
  const {
    data: deliverySchedules,
    isPending,
    isError,
    failureReason,
  } = useQuery({
    queryKey: ["scheduledLogisticItems"],
    queryFn: async () => await deliveryCore.listSchedules("prepared"),
  });

  const scheduleItemsGroups = useMemo<GroupedDeliverySchedule[] | undefined>(() => {
    if (!deliverySchedules) return undefined;

    return deliverySchedules.reduce<GroupedDeliverySchedule[]>((items, curr) => {
      if (!curr.shipDate || !curr.vehicle) return items;
      const formattedDate = curr.shipDate;

      let foundItem = items.find((item) => item.date === formattedDate && item.vechicle.id === curr.vehicle!.id);

      if (!foundItem) {
        foundItem = {
          date: formattedDate,
          vechicle: curr.vehicle!,
          schedules: [],
        };
        items.push(foundItem);
      }

      foundItem.schedules.push(curr);

      return items;
    }, []);
  }, [deliverySchedules]);

  return { isPending, isError, failureReason, data: scheduleItemsGroups };
}

export function useDeliveryStatusOptions() {
  const { t, msg } = useTranslation();

  return [
    { value: "scheduled", label: t(msg.delivery.scheduled), css: "text-white bg-gray-500" },
    { value: "preparing", label: t(msg.delivery.preparing), css: "text-white bg-blue-700" },
    { value: "onDelivery", label: t(msg.delivery.onDelivery), css: "text-white bg-[#2e9cdb]" },
    { value: "canPickup", label: t(msg.delivery.canPickup), css: "text-white bg-yellow-500" },
    { value: "delivered", label: t(msg.order.delivered), css: "text-white bg-green-700" },
    { value: "cancelled", label: t(msg.delivery.cancelled), css: "text-white bg-red-400" },
  ];
}
